// src/components/Signup.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { signUp, confirmSignUp } from '@aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import CourseInterestModal from './CourseInterestModal';

function Signup() {
    const { t } = useTranslation();
    const [step, setStep] = useState('signup'); // 'signup' or 'confirm'
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [confirmationCode, setConfirmationCode] = useState('');
    const [savedUsername, setSavedUsername] = useState(''); // Store username for verification
    
    // Additional user fields
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [address1, setAddress1] = useState('');
    const [address2, setAddress2] = useState('');
    const [city, setCity] = useState('');
    const [county, setCounty] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [country, setCountry] = useState('IE');
    const [gender, setGender] = useState('');
    const [dateOfBirth, setDateOfBirth] = useState('');
    const [userType, setUserType] = useState('student'); // Default to student
    const [courseInterest, setCourseInterest] = useState([]); // Store selected courses
    const [showCourseModal, setShowCourseModal] = useState(false);
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Check URL parameters on component mount
    useEffect(() => {
        const verifyParam = searchParams.get('verify');
        const usernameParam = searchParams.get('username');
        
        if (verifyParam === 'true') {
            setStep('confirm');
            if (usernameParam) {
                setSavedUsername(usernameParam);
            }
        }
    }, [searchParams]);

    // Enhanced phone number formatter to E.164 format
    const formatPhoneNumberToE164 = (phone, countryCode) => {
        if (!phone) return '';
        
        // Remove all non-digit characters except the leading +
        let cleaned = phone.replace(/[^\d+]/g, '');
        
        // If the number already starts with +, assume it's already in international format
        if (cleaned.startsWith('+')) {
            return cleaned;
        }
        
        // Country-specific formatting based on the selected country
        const countryFormats = {
            'IE': { code: '+353', trunkPrefix: '0', minLength: 9, maxLength: 9 },
            'GB': { code: '+44', trunkPrefix: '0', minLength: 10, maxLength: 10 },
            'US': { code: '+1', trunkPrefix: '1', minLength: 10, maxLength: 10 },
            'FR': { code: '+33', trunkPrefix: '0', minLength: 9, maxLength: 9 },
            'DE': { code: '+49', trunkPrefix: '0', minLength: 10, maxLength: 11 },
            'ES': { code: '+34', trunkPrefix: '', minLength: 9, maxLength: 9 },
            'IT': { code: '+39', trunkPrefix: '0', minLength: 9, maxLength: 10 }
        };
        
        const format = countryFormats[countryCode] || countryFormats['IE'];
        
        // Remove trunk prefix if present (e.g., leading 0 in Ireland/UK)
        if (format.trunkPrefix && cleaned.startsWith(format.trunkPrefix)) {
            cleaned = cleaned.substring(format.trunkPrefix.length);
        }
        
        // If the number is too short or too long, return it with the country code anyway
        // Cognito will validate and provide a more specific error
        if (cleaned.length < format.minLength || cleaned.length > format.maxLength) {
            // Still format it, but it might be invalid
            return format.code + cleaned;
        }
        
        // Add the country code
        return format.code + cleaned;
    };

    const handleCourseInterestSubmit = (selectedCourses) => {
        setCourseInterest(selectedCourses);
        setShowCourseModal(false);
        // Stay on signup form - don't move to confirmation
    };

    const handleCourseModalClose = () => {
        setShowCourseModal(false);
        // Stay on signup form - allow user to continue filling out details
    };

    const handleSignup = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        // Validate form
        if (password !== confirmPassword) {
            setError(t('auth.errors.passwordMismatch'));
            setLoading(false);
            return;
        }

        try {
            console.log('Signing up user:', username);
            
            // Format phone number to E.164 format
            let formattedPhoneNumber = '';
            if (phoneNumber) {
                formattedPhoneNumber = formatPhoneNumberToE164(phoneNumber, country);
                console.log('Formatted phone number:', formattedPhoneNumber);
                
                // Basic validation - should start with + and have at least 10 digits total
                if (!formattedPhoneNumber.match(/^\+\d{10,15}$/)) {
                    setError(t('auth.errors.invalidPhone'));
                    setLoading(false);
                    return;
                }
            }
            
            // Ensure country is exactly 2 characters
            const formattedCountry = country && country.length === 2 ? country : 'IE';
            
            // Call Amplify's signUp method with extended attributes
            const signUpResult = await signUp({
                username,
                password,
                options: {
                    userAttributes: {
                        email,
                        given_name: firstName,
                        family_name: lastName,
                        ...(formattedPhoneNumber && { phone_number: formattedPhoneNumber }), // Only include if provided
                        // Format address as a complete string for Cognito's standard attribute
                        address: `${address1}, ${address2 ? address2 + ', ' : ''}${city}, ${county ? county + ', ' : ''}${postalCode}, ${formattedCountry}`,
                        // Store individual components as custom attributes for easier retrieval
                        'custom:address1': address1,
                        'custom:address2': address2 || '', // Ensure empty string rather than undefined
                        'custom:city': city,
                        'custom:county': county || '', // Ensure empty string rather than undefined
                        'custom:postal_code': postalCode,
                        'custom:country': formattedCountry, // Use formatted country
                        // Only include gender and DOB for students
                        ...(userType === 'student' && { 'custom:gender': gender || '' }),
                        ...(userType === 'student' && { 'custom:date_of_birth': dateOfBirth || '' }),
                        'custom:user_type': userType, // Add user type for Lambda to detect Parent/Guardian
                        // Add course interest for guardians
                        ...(userType === 'parent' && courseInterest.length > 0 && { 
                            'custom:course_interest': courseInterest.join(',') 
                        })
                    },
                    autoSignIn: true // Enables auto sign-in after confirmation
                }
            });
            
            console.log('Sign up result:', signUpResult);
            
            // Save username for verification step
            setSavedUsername(username);
            
            setSuccessMessage(t('auth.signup.successMessage'));
            
            // Move to confirmation step
            setStep('confirm');
        } catch (err) {
            console.error("Signup failed:", err);
            
            // Handle different error types
            if (err.name === 'UsernameExistsException') {
                setError(t('auth.errors.usernameExists'));
            } else if (err.name === 'InvalidPasswordException') {
                setError(t('auth.errors.invalidPassword', { message: err.message }));
            } else if (err.name === 'InvalidParameterException') {
                if (err.message.includes('email')) {
                    setError(t('auth.errors.invalidEmail'));
                } else if (err.message.includes('phone')) {
                    setError(t('auth.errors.invalidPhoneFormat'));
                } else {
                    setError(t('auth.errors.invalidInput', { message: err.message }));
                }
            } else {
                setError(err.message || t('auth.errors.signupFailed'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmation = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        try {
            console.log('Confirming signup for user:', username);
            
            // Call Amplify's confirmSignUp method
            const confirmResult = await confirmSignUp({
                username: savedUsername || username, // Use saved username from signup step
                confirmationCode
            });
            
            console.log('Confirmation result:', confirmResult);
            
            setSuccessMessage(t('auth.confirm.successMessage'));
            
            // Redirect to login page after a short delay
            setTimeout(() => {
                navigate('/login');
            }, 2000);
        } catch (err) {
            console.error("Confirmation failed:", err);
            
            // Handle different error types
            if (err.name === 'CodeMismatchException') {
                setError(t('auth.errors.codeMismatch'));
            } else if (err.name === 'ExpiredCodeException') {
                setError(t('auth.errors.codeExpired'));
            } else {
                setError(err.message || t('auth.errors.confirmFailed'));
            }
        } finally {
            setLoading(false);
        }
    };

    const resendConfirmationCode = async () => {
        try {
            // This would be implemented with Cognito's resendConfirmationCode method
            console.log('Resending confirmation code for:', username);
            setSuccessMessage(t('auth.confirm.codeResent'));
        } catch (error) {
            console.error("Failed to resend code:", error);
            setError(t('auth.errors.resendFailed'));
        }
    };

    // Helper function to get placeholder based on country
    const getPhonePlaceholder = () => {
        const placeholders = {
            'IE': '087 123 4567',
            'GB': '07123 456789',
            'US': '(555) 123-4567',
            'FR': '06 12 34 56 78',
            'DE': '0151 12345678',
            'ES': '612 345 678',
            'IT': '312 345 6789'
        };
        return placeholders[country] || placeholders['IE'];
    };

    return (
        <div className="max-w-2xl mx-auto my-12">
            <div className="bg-white p-8 shadow-lg rounded-lg">
                <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
                    {step === 'signup' ? t('auth.signup.title') : t('auth.confirm.title')}
                </h2>
                
                {/* TEMPORARILY DISABLED: Course Interest Modal for Guardian accounts */}
                {/* <CourseInterestModal
                    isOpen={showCourseModal}
                    onClose={handleCourseModalClose}
                    onSubmit={handleCourseInterestSubmit}
                    loading={loading}
                /> */}
                
                {error && (
                    <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
                        {error}
                    </div>
                )}
                
                {successMessage && (
                    <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg">
                        {successMessage}
                    </div>
                )}
                
                {step === 'signup' ? (
                    <form onSubmit={handleSignup}>
                        {/* Account Type Selection - IMPORTANT */}
                        <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                            <h3 className="text-lg font-semibold text-gray-800 mb-3">
                                {t('auth.signup.selectAccountType')} <span className="text-red-500">*</span>
                            </h3>
                            <p className="text-sm text-gray-600 mb-4">
                                {t('auth.signup.accountTypeImportant')}
                            </p>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div 
                                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                        userType === 'student' 
                                            ? 'border-blue-500 bg-blue-100' 
                                            : 'border-gray-300 bg-white hover:border-blue-300'
                                    }`}
                                    onClick={() => setUserType('student')}
                                >
                                    <label className="flex items-start cursor-pointer">
                                        <input
                                            type="radio"
                                            name="userType"
                                            value="student"
                                            checked={userType === 'student'}
                                            onChange={(e) => setUserType(e.target.value)}
                                            className="mt-1 mr-3"
                                        />
                                        <div className="flex-1">
                                            <div className="font-semibold text-gray-800 mb-2">
                                                {t('auth.signup.accountTypeStudent')}
                                            </div>
                                            <p className="text-sm text-gray-600">
                                                {t('auth.signup.studentDescription')}
                                            </p>
                                            <ul className="text-xs text-gray-500 mt-2 space-y-1">
                                                <li>• {t('auth.signup.studentBookSelf')}</li>
                                                <li>• {t('auth.signup.studentPaySelf')}</li>
                                                <li>• {t('auth.signup.studentContact')}</li>
                                            </ul>
                                        </div>
                                    </label>
                                </div>
                                
                                <div 
                                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                        userType === 'parent' 
                                            ? 'border-blue-500 bg-blue-100' 
                                            : 'border-gray-300 bg-white hover:border-blue-300'
                                    }`}
                                    onClick={() => {
                                        setUserType('parent');
                                        // TEMPORARILY DISABLED: Course interest modal
                                        // setShowCourseModal(true);
                                    }}
                                >
                                    <label className="flex items-start cursor-pointer">
                                        <input
                                            type="radio"
                                            name="userType"
                                            value="parent"
                                            checked={userType === 'parent'}
                                            onChange={(e) => {
                                                setUserType(e.target.value);
                                                // TEMPORARILY DISABLED: Course interest modal
                                                // if (e.target.value === 'parent') {
                                                //     setShowCourseModal(true);
                                                // }
                                            }}
                                            className="mt-1 mr-3"
                                        />
                                        <div className="flex-1">
                                            <div className="font-semibold text-gray-800 mb-2">
                                                {t('auth.signup.accountTypeParent')}
                                            </div>
                                            <p className="text-sm text-gray-600">
                                                {t('auth.signup.guardianDescription')}
                                            </p>
                                            <ul className="text-xs text-gray-500 mt-2 space-y-1">
                                                <li>• {t('auth.signup.guardianBookChildren')}</li>
                                                <li>• {t('auth.signup.guardianManageMultiple')}</li>
                                                <li>• {t('auth.signup.guardianPayments')}</li>
                                                <li>• {t('auth.signup.guardianContact')}</li>
                                            </ul>
                                        </div>
                                    </label>
                                </div>
                            </div>
                            
                            {/* Show selected courses if Guardian/Parent is selected and courses are chosen */}
                            {userType === 'parent' && courseInterest.length > 0 && (
                                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded">
                                    <p className="text-sm font-semibold text-green-800 mb-1">
                                        Selected Courses of Interest:
                                    </p>
                                    <p className="text-xs text-green-700">
                                        {courseInterest.join(', ')}
                                    </p>
                                    {/* TEMPORARILY DISABLED: Change selection button */}
                                    {/* <button
                                        type="button"
                                        onClick={() => setShowCourseModal(true)}
                                        className="mt-2 text-xs text-green-600 hover:text-green-800 underline"
                                    >
                                        Change Selection
                                    </button> */}
                                </div>
                            )}
                            
                            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                                <p className="text-xs text-yellow-800">
                                    <strong>{t('auth.signup.importantNote')}:</strong> {t('auth.signup.accountTypeCannotChange')}
                                </p>
                            </div>
                        </div>

                        {/* Account Information Section */}
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-700 mb-4">{t('auth.signup.accountInfo')}</h3>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-gray-700 mb-2" htmlFor="username">
                                        {t('auth.signup.emailUsername')} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        id="username"
                                        type="text"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        className="border border-gray-300 p-3 w-full rounded"
                                        required
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-gray-700 mb-2" htmlFor="email">
                                        {t('auth.signup.confirmEmail')} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        id="email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="border border-gray-300 p-3 w-full rounded"
                                        required
                                    />
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <div>
                                    <label className="block text-gray-700 mb-2" htmlFor="password">
                                        {t('auth.signup.password')} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        id="password"
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="border border-gray-300 p-3 w-full rounded"
                                        required
                                        minLength="8"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        {t('auth.signup.passwordRequirements')}
                                    </p>
                                </div>
                                
                                <div>
                                    <label className="block text-gray-700 mb-2" htmlFor="confirmPassword">
                                        {t('auth.signup.confirmPassword')} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        id="confirmPassword"
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="border border-gray-300 p-3 w-full rounded"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Personal Information Section */}
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-700 mb-4">{t('auth.signup.personalInfo')}</h3>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-gray-700 mb-2" htmlFor="firstName">
                                        {t('auth.signup.firstName')} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        id="firstName"
                                        type="text"
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        className="border border-gray-300 p-3 w-full rounded"
                                        required
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-gray-700 mb-2" htmlFor="lastName">
                                        {t('auth.signup.lastName')} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        id="lastName"
                                        type="text"
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                        className="border border-gray-300 p-3 w-full rounded"
                                        required
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-gray-700 mb-2" htmlFor="phoneNumber">
                                        {t('auth.signup.phoneNumber')}
                                    </label>
                                    <input
                                        id="phoneNumber"
                                        type="tel"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.target.value)}
                                        className="border border-gray-300 p-3 w-full rounded"
                                        placeholder={getPhonePlaceholder()}
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        {t('auth.signup.phoneHelp')}
                                    </p>
                                </div>
                                
                                {/* Only show gender and DOB for students */}
                                {userType === 'student' && (
                                    <>
                                        <div>
                                            <label className="block text-gray-700 mb-2" htmlFor="gender">
                                                {t('auth.signup.gender')} <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                id="gender"
                                                value={gender}
                                                onChange={(e) => setGender(e.target.value)}
                                                className="border border-gray-300 p-3 w-full rounded"
                                                required
                                            >
                                                <option value="">{t('common.pleaseSelect')}</option>
                                                <option value="female">{t('student.female')}</option>
                                                <option value="male">{t('student.male')}</option>
                                            </select>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {t('auth.signup.genderHelp')}
                                            </p>
                                        </div>
                                        
                                        <div>
                                            <label className="block text-gray-700 mb-2" htmlFor="dateOfBirth">
                                                {t('auth.signup.dateOfBirth')} <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                id="dateOfBirth"
                                                type="date"
                                                value={dateOfBirth}
                                                onChange={(e) => setDateOfBirth(e.target.value)}
                                                className="border border-gray-300 p-3 w-full rounded"
                                                max={new Date().toISOString().split('T')[0]}
                                                required
                                            />
                                            <p className="text-xs text-gray-500 mt-1">
                                                {t('auth.signup.dobHelp')}
                                            </p>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Billing Address Section */}
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-700 mb-4">{t('auth.signup.billingAddress')}</h3>
                            <p className="text-sm text-gray-600 mb-4">
                                {t('auth.signup.billingAddressHelp')}
                            </p>
                            
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-gray-700 mb-2" htmlFor="address1">
                                        {t('auth.signup.addressLine1')} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        id="address1"
                                        type="text"
                                        value={address1}
                                        onChange={(e) => setAddress1(e.target.value)}
                                        className="border border-gray-300 p-3 w-full rounded"
                                        placeholder={t('auth.signup.addressLine1Placeholder')}
                                        required
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-gray-700 mb-2" htmlFor="address2">
                                        {t('auth.signup.addressLine2')}
                                    </label>
                                    <input
                                        id="address2"
                                        type="text"
                                        value={address2}
                                        onChange={(e) => setAddress2(e.target.value)}
                                        className="border border-gray-300 p-3 w-full rounded"
                                        placeholder={t('auth.signup.addressLine2Placeholder')}
                                    />
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-gray-700 mb-2" htmlFor="city">
                                            {t('auth.signup.city')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            id="city"
                                            type="text"
                                            value={city}
                                            onChange={(e) => setCity(e.target.value)}
                                            className="border border-gray-300 p-3 w-full rounded"
                                            placeholder="Dublin"
                                            required
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-gray-700 mb-2" htmlFor="county">
                                            {t('auth.signup.county')}
                                        </label>
                                        <input
                                            id="county"
                                            type="text"
                                            value={county}
                                            onChange={(e) => setCounty(e.target.value)}
                                            className="border border-gray-300 p-3 w-full rounded"
                                            placeholder="Dublin"
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-gray-700 mb-2" htmlFor="postalCode">
                                            {t('auth.signup.postalCode')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            id="postalCode"
                                            type="text"
                                            value={postalCode}
                                            onChange={(e) => setPostalCode(e.target.value)}
                                            className="border border-gray-300 p-3 w-full rounded"
                                            placeholder="D01 1234"
                                            required
                                        />
                                    </div>
                                </div>
                                
                                <div>
                                    <label className="block text-gray-700 mb-2" htmlFor="country">
                                        {t('auth.signup.country')} <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        id="country"
                                        value={country}
                                        onChange={(e) => setCountry(e.target.value)}
                                        className="border border-gray-300 p-3 w-full rounded"
                                        required
                                    >
                                        <option value="IE">{t('countries.ireland')}</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        
                        <button
                            type="submit"
                            className={`w-full py-3 px-4 rounded text-white font-medium ${
                                loading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                            disabled={loading}
                        >
                            {loading ? t('auth.signup.creatingAccount') : t('auth.signup.signUpButton')}
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleConfirmation}>
                        {!savedUsername && (
                            <div className="mb-6">
                                <label className="block text-gray-700 mb-2" htmlFor="verifyUsername">
                                    {t('auth.signup.emailUsername')}
                                </label>
                                <input
                                    id="verifyUsername"
                                    type="text"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="border border-gray-300 p-3 w-full rounded"
                                    placeholder={t('auth.signup.enterEmail')}
                                    required
                                />
                            </div>
                        )}
                        
                        {savedUsername && (
                            <div className="mb-6 p-3 bg-gray-100 rounded">
                                <p className="text-sm text-gray-600">
                                    {t('auth.confirm.verifyingAccount')}: <strong>{savedUsername}</strong>
                                </p>
                            </div>
                        )}
                        
                        <div className="mb-6">
                            <label className="block text-gray-700 mb-2" htmlFor="confirmationCode">
                                {t('auth.confirm.verificationCode')}
                            </label>
                            <input
                                id="confirmationCode"
                                type="text"
                                value={confirmationCode}
                                onChange={(e) => setConfirmationCode(e.target.value)}
                                className="border border-gray-300 p-3 w-full rounded"
                                required
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                {t('auth.confirm.enterCode')}
                            </p>
                        </div>
                        
                        <button
                            type="submit"
                            className={`w-full py-3 px-4 rounded text-white font-medium ${
                                loading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                            disabled={loading}
                        >
                            {loading ? t('auth.confirm.verifying') : t('auth.confirm.verifyButton')}
                        </button>
                        
                        <div className="mt-4 text-center">
                            <button
                                type="button"
                                onClick={resendConfirmationCode}
                                className="text-blue-600 hover:underline text-sm"
                                disabled={!savedUsername && !username}
                            >
                                {t('auth.confirm.resendCode')}
                            </button>
                        </div>
                    </form>
                )}
                
                <div className="mt-6 text-center">
                    {step === 'signup' ? (
                        <p className="text-gray-600">
                            {t('auth.signup.alreadyHaveAccount')}{' '}
                            <Link to="/login" className="text-blue-600 hover:underline">
                                {t('navigation.login')}
                            </Link>
                        </p>
                    ) : (
                        <>
                            <p className="text-gray-600 mb-2">
                                {t('auth.confirm.differentEmail')}{' '}
                                <button
                                    type="button"
                                    onClick={() => {
                                        setStep('signup');
                                        setError(null);
                                        setSuccessMessage(null);
                                    }}
                                    className="text-blue-600 hover:underline"
                                >
                                    {t('auth.confirm.backToSignup')}
                                </button>
                            </p>
                            <p className="text-gray-600">
                                {t('auth.confirm.alreadyVerified')}{' '}
                                <Link to="/login" className="text-blue-600 hover:underline">
                                    {t('navigation.login')}
                                </Link>
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Signup;